import React, { useEffect, useRef, useState } from "react";
import { useT } from "@/lib/i18n";

function useInView(opts = { threshold: 0.2 }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setSeen(true);
        io.disconnect();
      }
    }, opts);
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
}

function Counter({ to, duration = 1500, suffix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return (
    <span className="zp-num text-4xl sm:text-5xl">
      {n.toLocaleString()}
      {suffix}
    </span>
  );
}

export default function TrustMetrics() {
  const [ref, seen] = useInView();
  const { t } = useT();
  const METRICS = [
    { value: 48000, suffix: "+", label: t("metrics.shipments") },
    { value: 62, suffix: "", label: t("metrics.countries") },
    { value: 18500, suffix: "+", label: t("metrics.clearances") },
    { value: 240, suffix: "+", label: t("metrics.partners") },
    { value: 2, suffix: "h", label: t("metrics.response") },
  ];
  return (
    <section
      ref={ref}
      data-testid="trust-metrics"
      className="relative py-20 border-y border-[color:var(--zp-border)] zp-band-soft"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="zp-label">{t("metrics.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">
              {t("metrics.title")}
            </h2>
          </div>
          <p className="text-[color:var(--zp-text-2)] max-w-md text-sm">
            {t("metrics.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 border border-[color:var(--zp-border)] rounded-2xl overflow-hidden divide-y md:divide-y-0 md:divide-x divide-[color:var(--zp-border)] bg-white shadow-[0_20px_50px_-30px_rgba(10,37,64,0.12)]">
          {METRICS.map((m, i) => (
            <div
              key={m.label}
              data-testid={`trust-metric-${i}`}
              className="px-5 py-7 sm:px-6 sm:py-8 flex flex-col gap-3 hover:bg-[color:var(--zp-bg-2)] transition-colors min-w-0"
            >
              <div className="overflow-hidden">
                {seen ? (
                  <Counter to={m.value} suffix={m.suffix} />
                ) : (
                  <span className="zp-num text-4xl sm:text-5xl">0{m.suffix}</span>
                )}
              </div>
              <div className="text-sm text-[color:var(--zp-text-2)]">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
